"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getUserById } from "@/actions/auth/user";
import { useCurrentUser } from "@/hooks/use-current-user";
import { usePathname, useRouter } from "next/navigation";

const IS_ONBOARDING_ACTIVE: boolean = false;

type ForcedRedirectionContextData = {
  isOnboarded: boolean;
  setIsOnboarded: (value: boolean) => void;
};

const ForcedRedirectionContext = createContext<
  ForcedRedirectionContextData | undefined
>(undefined);

export const useForcedRedirection = () => {
  const context = useContext(ForcedRedirectionContext);

  if (!context) {
    throw new Error(
      "useForcedRedirection must be used within a ForcedRedirectionProvider",
    );
  }

  return context!;
};

export const ForcedRedirectionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const user = useCurrentUser();

  const router = useRouter();
  const pathname = usePathname();

  const [isOnboarded, setIsOnboarded] = useState(false);
  const isOnboardedRef = useRef(isOnboarded);

  useEffect(() => {
    isOnboardedRef.current = isOnboarded;
  }, [isOnboarded]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    if (isOnboardedRef.current) {
      if (pathname === "/onboarding") {
        router.push("/");
      }

      return;
    }

    const checkOnboardingStatus = async () => {
      const dbUser = await getUserById(user.id!);

      if (!dbUser) {
        return;
      }

      if (dbUser.shouldCompleteOnboarding) {
        router.push("/onboarding");
      } else {
        setIsOnboarded(true);

        if (pathname === "/onboarding") {
          router.push("/");
        }
      }
    };

    if (IS_ONBOARDING_ACTIVE) {
      checkOnboardingStatus();
    }
  }, [user, pathname]);
  return (
    <ForcedRedirectionContext.Provider
      value={{
        isOnboarded,
        setIsOnboarded,
      }}
    >
      {children}
    </ForcedRedirectionContext.Provider>
  );
};
